import { useState } from "react";
import { useShallow } from "zustand/react/shallow";

import useRowKeyboardNavigation from "ds/components/Table/hooks/useKeyboardNavigation/useRowKeyboardNavigation";
import TableBodyRow from "ds/components/Table/Row";
import { TableSimpleStore } from "ds/components/TableSimple/types";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";
import { getItemId, isStickyLeft, isStickyRight } from "ds/components/TableSimple/helpers";
import { ConfigElement } from "types/generated";

import IntentProjectEnvironmentMountedFilesTableFileRow from "./FileRow";
import styles from "./styles.module.css";
import { INTENT_PROJECT_ENVIRONMENT_MOUNTED_FILES_TABLE_CELLS } from "./Cells";
import { INTENT_PROJECT_ENVIRONMENT_MOUNTED_FILES_COLUMN_ID } from "./constants";

type IntentProjectEnvironmentMountedFilesTableRowsProps = {
  data: ConfigElement[];
  store: TableSimpleStore;
  itemIdKey?: string;
};

const IntentProjectEnvironmentMountedFilesTableRows = ({
  data,
  store,
  itemIdKey = "id",
}: IntentProjectEnvironmentMountedFilesTableRowsProps) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const keyboardProps = useRowKeyboardNavigation({ isGroup: false });

  const { columns, isFirstColumnPinned } = store(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
      isFirstColumnPinned: state.isFirstColumnPinned,
    }))
  );

  const toggleRow = (itemId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  return (
    <>
      {data.map((item) => {
        const itemId = getItemId(item, itemIdKey);
        const firstRowKey = `${itemId}-file-info-row`;
        const secondRowKey = `${itemId}-file-body-row`;
        const fileContentCellKey = `${itemId}-file-body-cell`;
        const isExpanded = expandedRows.has(itemId);

        return [
          <TableBodyRow key={firstRowKey} {...keyboardProps}>
            {columns.map((column, i) => {
              const cellId = `${itemId}-${column.id}`;
              const Cell =
                INTENT_PROJECT_ENVIRONMENT_MOUNTED_FILES_TABLE_CELLS[
                  column.id as INTENT_PROJECT_ENVIRONMENT_MOUNTED_FILES_COLUMN_ID
                ];
              const stickyLeft = isStickyLeft(i, isFirstColumnPinned);
              const stickyRight = isStickyRight(i, columns.length);

              return (
                <Cell
                  key={cellId}
                  id={cellId}
                  stickyLeft={stickyLeft}
                  stickyRight={stickyRight}
                  item={item}
                  isFirst={i === 0}
                  onPress={() => toggleRow(itemId)}
                  isExpanded={isExpanded}
                />
              );
            })}
          </TableBodyRow>,
          isExpanded && (
            <TableBodyRow key={secondRowKey} {...keyboardProps}>
              <td id={fileContentCellKey} colSpan={columns.length} className={styles.colSpanCell}>
                <IntentProjectEnvironmentMountedFilesTableFileRow item={item} />
              </td>
            </TableBodyRow>
          ),
        ];
      })}
    </>
  );
};

export default IntentProjectEnvironmentMountedFilesTableRows;
